// Mock data for Outwrite.ai promotional landing page

export const heroData = {
  headline: "Be the Answer AI Recommends.",
  subheadline: "Every Time.",
  description: "Get your brand cited by ChatGPT, Claude, Gemini & Perplexity. Create AI-optimized content that ranks in AI search results in hours, not months.",
  ctaText: "Start Free Today",
  ctaLink: "https://outwrite.ai?ref=ntg2yzy",
  secondaryCta: "Book a Demo",
  aiModels: ["ChatGPT", "Claude", "Gemini", "Perplexity"]
};

export const statsData = [
  { value: "500%", label: "Average Citation Growth" },
  { value: "3 Weeks", label: "To First Client" },
  { value: "60 Sec", label: "Content Generation" },
  { value: "30+", label: "AI Citations in 30 Days" }
];

export const howItWorksData = [
  {
    step: "01",
    title: "Discover Citation Opportunities",
    description: "Scan your site or enter any topic to see who's being cited by AI and what content drives their visibility.",
    highlight: "Track citations in 30 seconds"
  },
  {
    step: "02",
    title: "AI Creates Better Content",
    description: "Our AI analyzes what's working and generates superior, citation-optimized content in under 60 seconds.",
    highlight: "Outperforms competitors automatically"
  },
  {
    step: "03",
    title: "Publish & Get Cited",
    description: "One-click export to WordPress & Shopify, then watch AI systems cite your expertise.",
    highlight: "Direct CMS integration"
  },
  {
    step: "04",
    title: "Track Your AI Visibility",
    description: "Monitor your citations across all AI platforms and watch your visibility grow over time.",
    highlight: "Real-time tracking dashboard"
  }
];

export const featuresData = [
  {
    icon: "Search",
    title: "Citation Discovery",
    description: "See exactly what content is getting cited by AI models and why."
  },
  {
    icon: "Sparkles",
    title: "AI Content Generation",
    description: "Create citation-optimized articles in under 60 seconds."
  },
  {
    icon: "BarChart3",
    title: "Visibility Tracking",
    description: "Monitor your brand's AI citations across all major platforms."
  },
  {
    icon: "Zap",
    title: "One-Click Publishing",
    description: "Export directly to WordPress, Shopify, and more."
  },
  {
    icon: "Calendar",
    title: "Content Calendar",
    description: "Plan and schedule your AI-optimized content strategy."
  },
  {
    icon: "Link",
    title: "Smart Internal Links",
    description: "Automatically build internal link structures for better visibility."
  }
];

export const benefitsData = [
  {
    title: "Built for Google's Guidelines",
    description: "Follows E-E-A-T principles, semantic structure, and user-first content standards.",
    icon: "Shield"
  },
  {
    title: "Optimized for AI Citations",
    description: "Q&A sections, answer fragments, and schema markup designed for AI retrieval.",
    icon: "Target"
  },
  {
    title: "No-Conflict Promise",
    description: "Content that ranks in traditional search while dominating AI-powered answers.",
    icon: "CheckCircle"
  },
  {
    title: "Blog Hosting Included",
    description: "Don't have a blog? We host it for you with one line of code.",
    icon: "Globe"
  }
];

export const testimonialsData = [
  {
    quote: "We've done SEO before but without much traction. With outwrite.ai we closed our first client from AI search within 3 weeks.",
    author: "Frederik Jakobsen",
    company: "Danish Lead Co.",
    result: "First client in 17 days"
  },
  {
    quote: "The visibility tracking alone is worth the price. We can see exactly which prompts our brand appears in.",
    author: "Sarah Chen",
    company: "GrowthLab Digital",
    result: "300% citation increase"
  },
  {
    quote: "Finally, a tool that understands the shift from traditional SEO to AI search optimization.",
    author: "Marcus Webb",
    company: "Elevate Marketing",
    result: "50+ AI citations monthly"
  }
];

export const pricingData = [
  {
    name: "Essentials",
    price: "$97",
    period: "/month",
    description: "Ideal for creators and small teams starting to build AI visibility",
    features: [
      "50 Articles & Social Posts per month",
      "10 prompts tracked",
      "1 Brand Kit",
      "Daily visibility tracking",
      "5,000 model runs/month"
    ],
    ctaText: "Start Free",
    popular: false
  },
  {
    name: "Starter",
    price: "$197",
    period: "/month",
    description: "Built for teams ready to publish consistently and grow faster",
    features: [
      "150 Articles & Social Posts per month",
      "25 prompts tracked",
      "5 Brand Kits",
      "Daily visibility tracking",
      "12,500 model runs/month"
    ],
    ctaText: "Get Started",
    popular: false
  },
  {
    name: "Pro",
    price: "$399",
    period: "/month",
    description: "For agencies and brands scaling AI-driven content visibility",
    features: [
      "300 Articles & Social Posts per month",
      "100 prompts tracked",
      "10 Brand Kits",
      "Daily visibility tracking",
      "50,000 model runs/month"
    ],
    ctaText: "Get Started",
    popular: true
  }
];

export const faqData = [
  {
    question: "What exactly do I get with outwrite.ai?",
    answer: "You get a complete AI visibility platform including content generation, citation tracking across ChatGPT, Claude, Gemini, and Perplexity, plus one-click publishing to your CMS."
  },
  {
    question: "How does this actually help my business?",
    answer: "When AI assistants recommend your brand in response to customer queries, you get qualified leads without traditional advertising. Our users close their first AI-sourced client within weeks."
  },
  {
    question: "Is this just another AI writing tool?",
    answer: "No. While we generate content, our focus is on getting that content CITED by AI models. Traditional AI writers don't optimize for AI search visibility."
  },
  {
    question: "Can I track my content's AI visibility over time?",
    answer: "Yes! Our dashboard shows exactly which prompts cite your brand, how often, and on which platforms. Watch your visibility grow in real-time."
  },
  {
    question: "Do I need technical skills to use this?",
    answer: "Not at all. Just describe what you want to write about in natural language, and our AI handles the rest. Publishing is one-click to WordPress or Shopify."
  },
  {
    question: "Can I try this before committing?",
    answer: "Absolutely! Start with a 7-day free trial on the Essentials plan. No credit card required."
  }
];

export const timelineData = [
  {
    day: "Day 1",
    title: "Setup Complete",
    description: "Account created, content generated, published to website",
    metric: "3 min setup time"
  },
  {
    day: "Day 30",
    title: "30+ AI Citations",
    description: "Active on ChatGPT, Perplexity, and Gemini",
    metric: "+500% growth"
  },
  {
    day: "Day 60",
    title: "3 New Clients",
    description: "Closed from AI referrals",
    metric: "$15k revenue"
  }
];

export const ctaLink = "https://outwrite.ai?ref=ntg2yzy";
